import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  Video,
  Music,
  Image as ImageIcon,
  Package,
  File,
  ShoppingCart,
  Clock,
  Star,
  User,
} from "lucide-react";
import { DigitalContent, formatPrice, CONTENT_TYPE_LABELS } from "@/types/digitalContent";
import { getDigitalContentForPurchase } from "@/services/digitalContent/publicDigitalContentService";
import { PurchaseConfirmationModal } from "@/components/digitalContent/PurchaseConfirmationModal";
import { useRole } from "@/hooks/useRole";
import { useNavigate } from "react-router-dom";

interface PublicDigitalContentCardProps {
  content: DigitalContent;
  showMentor?: boolean;
}

const getContentTypeIcon = (contentType: string) => {
  const icons = {
    pdf: <FileText className="h-4 w-4" />,
    video: <Video className="h-4 w-4" />,
    audio: <Music className="h-4 w-4" />,
    image: <ImageIcon className="h-4 w-4" />,
    zip: <Package className="h-4 w-4" />,
    document: <File className="h-4 w-4" />,
  };
  return icons[contentType as keyof typeof icons] || <File className="h-4 w-4" />;
};

const getContentTypeColor = (contentType: string): string => {
  const colors = {
    pdf: "bg-red-500/20 text-red-400 border-red-500/30",
    video: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    audio: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    image: "bg-green-500/20 text-green-400 border-green-500/30",
    zip: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    document: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  };
  return colors[contentType as keyof typeof colors] || colors.document;
};

export const PublicDigitalContentCard: React.FC<PublicDigitalContentCardProps> = ({
  content,
  showMentor = true,
}) => {
  const [selectedContent, setSelectedContent] = useState<DigitalContent | null>(null);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [isLoadingPurchase, setIsLoadingPurchase] = useState(false);
  const { authUser } = useRole();
  const navigate = useNavigate();

  const handlePurchase = async () => {
    if (!authUser) {
      navigate("/login");
      return;
    }
    try {
      setIsLoadingPurchase(true);
      const contentData = await getDigitalContentForPurchase(content.id);
      if (contentData) {
        setSelectedContent(contentData);
        setIsPurchaseModalOpen(true);
      }
    } catch (error) {
      console.error("Erro ao carregar dados do conteúdo:", error);
    } finally {
      setIsLoadingPurchase(false);
    }
  };

  const handlePurchaseSuccess = () => {
    navigate("/painel-jogador/meus-conteudos");
  };

  return (
    <>
      <div className="bg-[#1A2233] border border-[#2D3748] rounded-lg overflow-hidden hover:border-[#00B6F0]/50 transition-colors flex flex-col h-full">
        {/* Imagem de Capa */}
        {content.cover_image_url ? (
          <div className="relative h-40 overflow-hidden">
            <img
              src={content.cover_image_url}
              alt={content.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A2233]/80 to-transparent" />
          </div>
        ) : (
          <div className="relative h-40 bg-gradient-to-br from-[#223342] to-[#1A2233] flex items-center justify-center">
            {getContentTypeIcon(content.content_type)}
          </div>
        )}

        <div className="p-4 flex flex-col flex-1">
          {/* Tipo e Preço */}
          <div className="flex items-center justify-between mb-2">
            <Badge
              variant="outline"
              className={`text-xs ${getContentTypeColor(content.content_type)}`}
            >
              {getContentTypeIcon(content.content_type)}
              <span className="ml-1">{CONTENT_TYPE_LABELS[content.content_type]}</span>
            </Badge>

            <div className="text-lg font-bold text-[#00B6F0]">
              {formatPrice(content.price_cents)}
            </div>
          </div>

          {/* Título */}
          <h3 className="font-semibold text-white mb-2 line-clamp-2 leading-tight">
            {content.title}
          </h3>

          {/* Mentor */}
          {showMentor && content.mentor_name && (
            <button
              onClick={() => navigate(`/mentores/${content.mentor_id}`)}
              className="flex items-center gap-2 mb-2 text-sm text-gray-400 hover:text-[#00B6F0] transition-colors w-fit"
            >
              {content.mentor_image_url ? (
                <img
                  src={content.mentor_image_url}
                  alt={content.mentor_name}
                  className="h-5 w-5 rounded-full object-cover"
                />
              ) : (
                <User className="h-4 w-4" />
              )}
              <span className="truncate">{content.mentor_name}</span>
            </button>
          )}

          {/* Descrição */}
          <p className="text-sm text-gray-400 mb-3 line-clamp-2">{content.description}</p>

          {/* Informações adicionais */}
          <div className="flex items-center gap-3 text-xs text-gray-500 mb-4">
            {content.game?.name && (
              <Badge variant="outline" className="text-xs text-gray-300 border-[#2D3748]">
                {content.game.name}
              </Badge>
            )}
            {content.estimated_duration_minutes && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>{content.estimated_duration_minutes}min</span>
              </div>
            )}
            {content.download_count > 0 && (
              <div className="flex items-center gap-1">
                <Star className="h-3 w-3" />
                <span>{content.download_count} downloads</span>
              </div>
            )}
          </div>

          {/* Botão de Compra */}
          <div className="mt-auto">
            <Button
              onClick={handlePurchase}
              className="w-full bg-[#00B6F0] hover:bg-[#0099CC] text-black font-medium"
              disabled={isLoadingPurchase}
            >
              {isLoadingPurchase ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2" />
                  Carregando...
                </>
              ) : (
                <>
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  {content.price_cents === 0 ? "Obter Gratuito" : "Comprar Guia"}
                </>
              )}
            </Button>
            {!authUser && (
              <p className="text-xs text-gray-500 text-center mt-2">
                Faça login para adquirir este guia
              </p>
            )}
          </div>
        </div>
      </div>

      <PurchaseConfirmationModal
        isOpen={isPurchaseModalOpen}
        onClose={() => {
          setIsPurchaseModalOpen(false);
          setSelectedContent(null);
        }}
        content={selectedContent}
        onPurchaseSuccess={handlePurchaseSuccess}
      />
    </>
  );
};
